
import React, { useState, useEffect } from "react";
import { handleValidatePhoneNumber } from "../../../Utilities/general";
import { useToaster } from "../../../components/Toaster";
import { verifyphone, sendphoneverification } from "../../../store/actions/profileInfoActions";
import { AiOutlineLoading3Quarters, AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { BsArrowCounterclockwise } from 'react-icons/bs';
import CustomTooltip from '../../../components/Booking/CustomTooltip';
import { useDispatch } from "react-redux";
import { getUserProfile } from "../../../store/actions/userProfileActions";




const PersonalInfoScreen = ({
  firstName,
  lastName,
  email,
  phone,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  setJustRegisteredFromPersonalInfo,
  justRegisteredFromPersonalInfo,
  loginInfo
}) => {


  // verification code (only used in verification step)
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isInlineSending, setIsInlineSending] = useState(false);
  const [sendCooldown, setSendCooldown] = useState(0);
  const [showInlineVerification, setShowInlineVerification] = useState(false);
  const intervalRef = React.useRef(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const phoneInputRef = React.useRef(null);


  const { showToast } = useToaster();
  const dispatch = useDispatch();

  const verificationCodeValid = /^\d{6}$/.test(verificationCode);

  const phoneDigits = (phone || "").replace(/\D/g, "");
  const isPhoneComplete = phoneDigits.length === 10;

  const inlineResendDisabled = sendCooldown > 0;
  const resendTooltipText = inlineResendDisabled ? `Resend again (${sendCooldown}s)` : 'Resend again';

  const sendBtnClass = (disabled) => `w-full h-[44px] font-semibold text-[16px] ${disabled ? 'bg-[#9b98d6] text-[#ECECF8]' : 'bg-[#34318C] text-[#F6F6FD]'}`;
  const [isLoading, setIsLoading] = useState(false);

  const onVerifyCode = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    try {
      setIsVerifying(true);

      // use mock verification function instead of API
      const response = await verifyphone(phone, verificationCode);
      if (response.statusCode === 200 && response.data) {
        const currentUserEmail = response.data.email;
        dispatch(getUserProfile(currentUserEmail));
        setJustRegisteredFromPersonalInfo(true);
        localStorage.setItem('just_registered', 'true');
      }

      showToast({
        type: "success",
        message: "Code verified successfully!",
        duration: 4500,
      });

      try {
        const raw = localStorage.getItem("vision_verif");
        const obj = raw ? JSON.parse(raw) : {};
        obj.phone = phone || obj.phone;
        obj.isPhoneVerified = true;
        localStorage.setItem("vision_verif", JSON.stringify(obj));
      } catch (e) {
        console.error("localStorage set error", e);
      }

      setIsPhoneVerified(true);
      setShowInlineVerification(false);

    } catch (error) {
      console.error("Mock verification failed:", error);

      // Error message from API (e.g., "Invalid or expired code")
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Verification failed or code is incorrect";

      showToast({
        type: "error",
        message: errMsg,
        duration: 4500,
      });

      // clear code input so user can re-enter
      setVerificationCode("");
    } finally {
      setIsVerifying(false);
    }
  };



  const onSendCode = async () => {
    // delegate to shared send logic
    await performSend(false);
  };

  const performSend = async (fromInline = false) => {
    if ((fromInline ? isInlineSending : isSendingCode) || sendCooldown > 0) return;

    try {
      if (fromInline) setIsInlineSending(true);
      else setIsSendingCode(true);

      setVerificationCode("");

      // persist send timestamp so verification UI survives refresh
      try {
        const res = await sendphoneverification(phone);

        // If API returns error message
        if (res.status === false) {
          throw new Error(res.message || "Failed to send verification code");
        }

        console.log("send phone verification response", res);

        localStorage.setItem(
          "vision_verif",
          JSON.stringify({ sentAt: Date.now(), phone, isPhoneVerified: false })
        );

        // Show success toast only if success
        showToast({ type: "success", message: "Verification code sent!", duration: 4500 });

      } catch (e) {
        // Show error toast
        const errMsg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to send verification code";

        showToast({ type: "error", message: errMsg });
        console.error("API Error:", e);

        // Stop here if API failed (no cooldown, no UI changes)
        return;
      }

      setSendCooldown(60);
      setShowInlineVerification(true);

      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          setSendCooldown((s) => {
            if (s <= 1) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
              try {
                const currentRaw = localStorage.getItem("vision_verif");
                const currentObj = currentRaw ? JSON.parse(currentRaw) : {};
                currentObj.sentAt = 0;
                localStorage.setItem("vision_verif", JSON.stringify(currentObj));
              } catch (e) { }
              return 0;
            }
            return s - 1;
          });
        }, 1000);
      }

    } catch (err) {
      console.error("Error sending verification code:", err);
      setVerificationCode("");
    } finally {
      if (fromInline) setIsInlineSending(false);
      else setIsSendingCode(false);
    }
  };

  const onResendInlineClick = async () => {
    if (isInlineSending || isSendingCode || sendCooldown > 0) return;
    await performSend(true);
  };

  const clearPhoneAndReset = () => {
    // clear phone and related fields, reset verification flow
    setPhone('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setIsPhoneVerified(false);
    setSendCooldown(0);
    setShowInlineVerification(false);
    setVerificationCode('');
    try {
      const raw = localStorage.getItem('vision_verif');
      const obj = raw ? JSON.parse(raw) : {};
      obj.phone = '';
      obj.isPhoneVerified = false;
      obj.sentAt = 0;
      localStorage.setItem('vision_verif', JSON.stringify(obj));
    } catch (e) { }
    // focus input after enabling
    setTimeout(() => {
      phoneInputRef.current && phoneInputRef.current.focus();
    }, 0);
  };

  /// USE EFFECTS ////

  // cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('vision_verif');
      if (!raw) {
        // create default vision_verif if missing
        try {
          localStorage.setItem('vision_verif', JSON.stringify({ sentAt: 0, phone: phone || '', isPhoneVerified: false }));
        } catch (e) {
          console.error('localStorage set error', e);
        }
        return;
      }

      const obj = JSON.parse(raw);
      if (!obj) return;

      // restore phone if present
      if (obj.phone) {
        try { setPhone(obj.phone); } catch (e) { }
      }

      // if phone already verified, unlock fields
      if (obj.isPhoneVerified) {
        setIsPhoneVerified(true);
        setShowInlineVerification(false);
        setSendCooldown(0);
        return;
      }




      // otherwise, if sentAt exists and within 60s, restore countdown
      if (obj.sentAt) {
        const elapsed = Math.floor((Date.now() - obj.sentAt) / 1000);
        const remaining = 60 - elapsed;
        if (remaining > 0) {
          setSendCooldown(remaining);
          setShowInlineVerification(true);
          // start interval to count down
          if (!intervalRef.current) {
            intervalRef.current = setInterval(() => {
              setSendCooldown((s) => {
                if (s <= 1) {
                  clearInterval(intervalRef.current);
                  intervalRef.current = null;
                  try {
                    // do not remove vision_verif entirely; keep isPhoneVerified flag
                    const currentRaw = localStorage.getItem('vision_verif');
                    const currentObj = currentRaw ? JSON.parse(currentRaw) : {};
                    currentObj.sentAt = 0;
                    localStorage.setItem('vision_verif', JSON.stringify(currentObj));
                  } catch (e) { }
                  return 0;
                }
                return s - 1;
              });
            }, 1000);
          }
        }
      }
    } catch (e) {
      console.error('Error restoring verification state', e);
    }
  }, []);

  // auto-submit when 6-digit code entered
  useEffect(() => {
    if (showInlineVerification && verificationCodeValid && !isVerifying && !isSendingCode) {
      onVerifyCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode]);

  // NOTE: Do NOT auto-hide the verification UI when the cooldown ends.
  // The verification step should remain visible until the user explicitly
  // clears it via the cross icon or successfully verifies.


  return (
    <div className="px-[33px] font-instrument max-w-container">
      <p className="text-[#211F59] text-[24px] font-semibold mb-[8px]" style={{ lineHeight: "23px" }}>Personal info</p>
      <p className="text-[#211F59] text-[12px] mb-[18px]">We'll use this to contact you during your move.</p>

      <div>
        <div className="mt-[6px]">
          {/* Phone moved to top */}
          <div className="mt-[0px]">
            <label className="text-[#211F59] text-[14px] font-semibold mb-[8px] block">Phone number</label>
            {showInlineVerification && (
              <div>
                <p className="text-[#211F59] text-[12px] mb-[6px]">Verification code sent to {phone}</p>
              </div>
            )}

            <div className="bg-[#E6E6E6] rounded-[12px] h-[48px]  flex items-center transition-all duration-150 overflow-visible">
              <div className="flex items-center flex-1">
                <input
                  ref={phoneInputRef}
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    const newVal = e.target.value || '';
                    handleValidatePhoneNumber(e, setPhone);
                    // if phone changed, mark as unverified and persist
                    try {
                      const raw = localStorage.getItem('vision_verif');
                      const obj = raw ? JSON.parse(raw) : {};
                      obj.phone = newVal;
                      obj.isPhoneVerified = false;
                      obj.sentAt = obj.sentAt || 0;
                      localStorage.setItem('vision_verif', JSON.stringify(obj));
                    } catch (err) { }
                    setIsPhoneVerified(false);
                  }}
                  placeholder="Enter your phone number"
                  disabled={isPhoneVerified || isSendingCode || isVerifying || showInlineVerification}
                  className={`flex-1 bg-transparent placeholder:text-[#373559] placeholder:text-[14px] h-[48px] rounded-[12px] px-[13.5px] border-[1px] focus:border-[#7876A1] outline-none ${(isPhoneVerified || isSendingCode || isVerifying) ? 'opacity-60' : ''}`}
                />
                {(isPhoneVerified || showInlineVerification) ? (
                  <button
                    type="button"
                    onClick={clearPhoneAndReset}
                    className="ml-3 mr-3 flex-shrink-0 p-0 leading-none flex items-center justify-center"
                    aria-label="Clear phone"
                    disabled={loginInfo?.id && justRegisteredFromPersonalInfo}
                  >
                    <AiOutlineClose className="h-5 w-5 text-[#6B7280] block leading-none" />
                  </button>
                ) : (
                  // when verification UI is NOT shown, spinner belongs to verification input below
                  (!showInlineVerification && isVerifying) ? <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-[#34318C] mr-3" /> : null
                )}
              </div>
            </div>

            {showInlineVerification && (
              <div className="mt-[8px]">
                <div className="bg-[#E6E6E6] rounded-[12px] h-[48px] flex items-center transition-all duration-150 border-[1px] border-transparent focus-within:border-[#7876A1] overflow-visible">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => {
                      const val = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
                      setVerificationCode(val);
                    }}
                    placeholder="Enter verification code"
                    disabled={isSendingCode || isVerifying}
                    className={`flex-1 bg-transparent placeholder:text-[#373559] placeholder:text-[14px] h-[48px] px-[13.5px] outline-none ${isSendingCode || isVerifying ? 'opacity-60' : ''}`}
                  />
                  {(isInlineSending || isVerifying) ? (
                    <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-[#34318C] mr-3" />
                  ) : (
                    <CustomTooltip text={resendTooltipText}>
                      {inlineResendDisabled ? (
                        // show faded, non-interactive icon while cooldown runs
                        <span className="ml-3 mr-3 flex-shrink-0 flex items-center justify-center h-5 w-5 opacity-40 cursor-default" aria-hidden="true">
                          <BsArrowCounterclockwise className="block h-5 w-5 text-[#34318C] leading-none" />
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={onResendInlineClick}
                          className="ml-3 mr-3 flex-shrink-0 p-0 leading-none flex items-center justify-center h-5 w-5 opacity-100 cursor-pointer"
                          aria-label="Resend verification code"
                        >
                          <BsArrowCounterclockwise className="block h-5 w-5 text-[#34318C] leading-none" />
                        </button>
                      )}
                    </CustomTooltip>
                  )}
                </div>
              </div>
            )}

            { /* validation message removed per request */}

            <div className="my-[12px]">
              {!showInlineVerification && (
                isPhoneVerified ? (
                  <div className={"flex items-center justify-center gap-2 rounded-[15px] px-4 h-[44px] border-1 border-[#16A34A] bg-transparent"}>
                    <span className="text-[#16A34A] font-medium">Phone verified</span>
                    <AiOutlineCheck className="h-5 w-5 text-[#16A34A]" />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={onSendCode}
                    className={sendBtnClass(isSendingCode || sendCooldown > 0 || !isPhoneComplete) + " flex items-center justify-center gap-2 rounded-[15px]"}
                    disabled={isSendingCode || sendCooldown > 0 || !isPhoneComplete}
                  >
                    {isSendingCode ? (
                      <>
                        <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 text-white" />
                        <span className="text-xs">Sending...</span>
                      </>
                    ) : sendCooldown > 0 ? (
                      `Resend in ${sendCooldown}s`
                    ) : (
                      'Send verification code'
                    )}
                  </button>
                )
              )}
            </div>
          </div>

          <p className={`${!isPhoneVerified ? 'text-[#9b98d6]' : 'text-[#211F59]'} text-[14px] font-semibold mb-[10.5px]`}>Name & email</p>

          <div className="flex flex-row gap-2 mb-[7.5px]">
            <div className="md:w-1/2 w-full">
              <div className="bg-[#E6E6E6] rounded-[12px] h-[48px]  flex items-center transition-all duration-150">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  disabled={!isPhoneVerified || justRegisteredFromPersonalInfo}
                  className={`w-full bg-transparent placeholder:text-[#373559] placeholder:text-[14px] h-[48px] rounded-[12px] px-[13.5px] border-[1px] focus:border-[#7876A1] outline-none ${(!isPhoneVerified) ? 'opacity-60' : ''}`}

                />
              </div>
            </div>

            <div className="md:w-1/2 w-full">
              <div className="bg-[#E6E6E6] rounded-[12px] h-[48px]  flex items-center transition-all duration-150">
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  disabled={!isPhoneVerified || justRegisteredFromPersonalInfo}
                  className={`w-full bg-transparent placeholder:text-[#373559] placeholder:text-[14px] h-[48px] rounded-[12px] px-[13.5px] border-[1px] focus:border-[#7876A1] outline-none ${(!isPhoneVerified) ? 'opacity-60' : ''}`}

                />
              </div>
            </div>
          </div>

          <div>
            <div className="bg-[#E6E6E6] rounded-[12px] h-[48px] flex items-center transition-all duration-150">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                disabled={!isPhoneVerified || justRegisteredFromPersonalInfo}
                className={`w-full bg-transparent placeholder:text-[#373559] px-[13.5px]  placeholder:text-[14px] h-[48px] rounded-[12px]  border-[1px] focus:border-[#7876A1] outline-none ${(!isPhoneVerified) ? 'opacity-60' : ''}`}

              />
            </div>
          </div>



        </div>
      </div>



    </div>
  );
};

export default PersonalInfoScreen;